import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import AlertPopup from '../../../components/alert-popup';
import AnimationWrapper from '../../../components/animation/animationWrapper';
import Button from '../../../components/button';
import Table from '../../../components/table';
import ToastComponent from '../../../components/toast';
import Heading from '../../../components/typography';
import InitialValuesForm from '../../../constants/initialValues';
import { ParticipantBroadcastHeader } from '../../../constants/table-headers/participantBroadcastHeader';
import {
  useAdvanceBoolean,
  useBoolean,
  useGetTablePreferences,
  usePostTablePreferences,
  useWealthlaneCustomerApi,
} from '../../../hooks';
import useGetParticipantBroadcastMessageList from '../../../hooks/get/useGetParticipantBroadcastMessageList';
import { ParticipantBroadcastMessageService } from '../../../services/wealthlane-customer-services';
import { type ModalStatusType, type TableParamsType } from '../../../types/common';
import {
  SettingSystemUserButton,
  SettingSystemUserHeader,
  SystemUserContainer,
} from '../system-users/systemUserStyled';
import ParticipantBroadcastModel from './participantBroadcastModel';
import {
  useDynamicTable,
  DynamicTable,
  withDynamicTableContext,
  useTableSchemaLoader,
} from '@wealthlane/table-builder';

import Spinners from '../../../components/spinner';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../../../services/apiInterceptor';
// import useDynamicTable from '../../../packages/table-builder/hooks/useDynamicTable';
// import useTableSchemaLoader from '../../../packages/table-builder/hooks/useTableSchemaLoader';
// import DynamicTable from '../../../packages/table-builder';
// import { withDynamicTableContext } from '../../../packages/table-builder/HOC/withDynamicTableContext';

export const broadCastMessageInitialValues = {
  data: InitialValuesForm.participantBroadcastMessage,
  id: '',
};

const ParticipantBroadcast = (): JSX.Element => {
  const navigate = useNavigate();
  const { handleRequest } = useWealthlaneCustomerApi();
  const [modalStatus, setModalStatus] = useState<ModalStatusType>('Add');
  const { tableInfo, setTableInfo } = useDynamicTable();
  const [headerList, setHeaderList] = useState<string[]>([]);

  useEffect(() => {
    setTableInfo(InitialValuesForm.tableParams);
  }, []);
  // const [tableInfo, setTableInfo] = useState<TableParamsType>(InitialValuesForm.tableParams);
  const { data: list, status } = useGetParticipantBroadcastMessageList(handleRequest, tableInfo);
  const { value: isModalOpen, setFalse: closeModal, setTrue: openModal } = useBoolean(false);
  const [initialData, setInitialData] = useState(broadCastMessageInitialValues);
  const {
    value: isConfirmOpen,
    setFalse: closeConfirmModal,
    setTrue: openConfirmModal,
    customValue,
    setCustomValue,
  } = useAdvanceBoolean<string>(false);
  const queryClient = useQueryClient();
  const [modalLoading, setModalLoading] = useState(false);

  const { schemaData, isTableSchemaLoading } = useTableSchemaLoader({
    identifier: 'PARTICIPANT_BROADCAST_TABLE_LIST',
    baseUrl: process.env.REACT_APP_WEALTHLANE_API_ENDPOINT,
  });

  const { data: tablePreferences } = useGetTablePreferences(handleRequest, 'Ppt Broadcast');

  useEffect(() => {
    tablePreferences != null && setHeaderList([...tablePreferences]);
  }, [tablePreferences]);

  const { mutate: postTablePreference, isLoading: tablePrefLoading } =
    usePostTablePreferences(handleRequest);

  const handleSavePreference = (value: string[]): void => {
    const prefPayload = {
      tableName: 'Ppt Broadcast',
      preferences: value,
    };
    postTablePreference(prefPayload);
  };

  const tableActions = [
    {
      action: 'View',
      onClick: (id: string) => {
        setModalStatus('View');
        getTemplateMessage(id);
        openModal();
      },
      id: 'viewParticipantBroadcastAction',
    },
    {
      action: 'Edit',
      // as: 'Edit',
      onClick: (id: string) => {
        setModalStatus('Edit');
        getTemplateMessage(id);
        openModal();
      },
      id: 'editParticipantBroadcastAction',
    },
    {
      action: <div className='delete-label'>Delete</div>,
      // as: 'Delete',
      onClick: (id: string) => {
        setModalStatus('Delete');
        setCustomValue(id);
        openConfirmModal();
      },
      id: 'deleteParticipantBroadcastAction',
    },
  ];

  const getTemplateMessage = (id: string): any => {
    setModalLoading(true);
    void handleRequest(
      ParticipantBroadcastMessageService.getApiCustomerApiAppParticipantBroadcastMessageParticipantBroadcastMessageById(
        id
      )
    )
      .then((data: any) => {
        if (data != null) {
          const filteredData = {
            ...data,
            messageTemplateId: data?.messageTemplateId !== null ? data?.messageTemplateId : '11',
          };
          setInitialData({ id, data: filteredData });
        }
      })
      .finally(() => {
        setModalLoading(false);
      });
  };
  const onPrimaryClick = (values: Record<string, any>): void => {
    deleteBroadcastMessageById(customValue);
  };

  const deleteBroadcastMessageById = (id: string): void => {
    setModalLoading(true);
    handleRequest(
      ParticipantBroadcastMessageService.deleteApiCustomerApiAppParticipantBroadcastMessageParticipantBroadcastMessage(
        id
      )
    )
      .then((data: any) => {
        if (data != null) {
          toast.success(
            <ToastComponent label={'Success'} message={'Participant Broadcast Message deleted.'} />
          );
          void queryClient.invalidateQueries('getParticipantBroadcastMessageList');
          setModalLoading(false);
          closeConfirmModal();
        }
      })
      .catch(() => {
        setModalLoading(false);
        closeConfirmModal();
      })
      .finally(() => {
        setModalLoading(false);
        closeConfirmModal();
      });
  };

  const tableHeaderList = ParticipantBroadcastHeader({ actions: schemaData?.columns, tableActions })
    .filter((item) => item.isSubHeader)
    .map((item) => {
      // console.log('item', item);

      return {
        label: item.Header,
        value: item.accessor,
      };
    });

  const pptBroadcastHeader = (): any[] => {
    return ParticipantBroadcastHeader({ actions: schemaData?.columns, tableActions }).filter(
      (item) => item.isSubHeader === false || headerList.includes(item.accessor)
    );
  };

  const schema = React.useMemo(
    () => ({
      columns: pptBroadcastHeader(),
      options: schemaData?.options as any,
    }),
    [schemaData]
  );
  // const renderRightToolbarButton = () => {
  //   return (
  //     <Button
  //       as='Create'
  //       title='Create Message'
  //       variant='primary'
  //       icon={'add_circle_outline'}
  //       onClick={() => {
  //         navigate('/participants/form/create');
  //       }}
  //     />
  //   );
  // };
  if (isTableSchemaLoading) return <Spinners containerClassName='h-screen' />;

  return (
    <SystemUserContainer>
      <div className='flex justify-between items-center' id="participantBroadcastHeader">
        <SettingSystemUserHeader id="participantBroadcastTitle">
          <Heading variant='h3' title='Participant Broadcast' id="pageTitle" />
        </SettingSystemUserHeader>
        <SettingSystemUserButton className='xl:col-span-2 flex justify-start lg:justify-end' id="participantBroadcastButtonContainer">
          <Button
            // as='Create'
            variant={'primary'}
            title={'Create Message'}
            icon={'add_circle_outline'}
            id="createParticipantBroadcastButton"
            onClick={() => {
              setInitialData(broadCastMessageInitialValues);
              setModalStatus('Add');
              // setDataRow('');
              openModal();
            }}
          />
        </SettingSystemUserButton>
      </div>
      <AnimationWrapper>
        {isModalOpen && (
          <ParticipantBroadcastModel
            id="participantBroadcastModal"
            isModalOpen={isModalOpen}
            closeModal={closeModal}
            modalStatus={modalStatus}
            setModalStatus={setModalStatus}
            initialData={initialData}
            loading={modalLoading}
          //   tableInfo={tableInfo}
          //   setTableInfo={setTableInfo}
          //   dataRow={dataRow}
          />
        )}
      </AnimationWrapper>
      {isConfirmOpen && (
        <AlertPopup
          id="deleteParticipantBroadcastConfirmModal"
          isShow={isConfirmOpen}
          closeModal={closeConfirmModal}
          modalStatus={modalStatus}
          title={'this Message'}
          handleSubmit={onPrimaryClick}
          // setRemark={setRemark}
          // remark={remark}
          loading={modalLoading}
        />
      )}
      <DynamicTable
        id="participantBroadcastTable"
        schema={schema}
        data={list?.items ?? []}
        loading={status}
        totalCounts={list?.totalCount ?? 10}
        tableColumnList={tableHeaderList}
        selectedColumnList={headerList}
        tablePreferenceLoading={tablePrefLoading}
        handleColumnSave={handleSavePreference}
      />
      {/* <Table
        columns={ParticipantBroadcastHeader(actions)}
        data={list?.items ?? []}
        loading={status}
        showPagination
        totalCounts={list?.totalCount ?? 10}
        tableInfo={tableInfo}
        setTableInfo={setTableInfo}
      /> */}
    </SystemUserContainer>
  );
};

export default withDynamicTableContext(ParticipantBroadcast, false, axiosInstance);
